import {
	ToggleButton,
	ToggleButtonGroup,
	ToggleButtonGroupProps,
	ToggleButtonProps,
	alpha,
	useTheme,
} from "@mui/material";

export type QuickRangeToggleOption<TValue extends string | number> = {
	value: TValue;
	label: string;
	ariaLabel?: string;
};

type QuickRangeToggleProps<TValue extends string | number> = {
	value: TValue | "" | null;
	onChange: (value: TValue) => void;
	options: readonly QuickRangeToggleOption<TValue>[];
	ariaLabel?: string;
	groupProps?: Omit<ToggleButtonGroupProps, "value" | "onChange" | "exclusive">;
	buttonProps?: Omit<ToggleButtonProps, "value" | "children">;
};

export const QuickRangeToggle = <TValue extends string | number>({
	value,
	onChange,
	options,
	ariaLabel = "Quick range",
	groupProps,
	buttonProps,
}: QuickRangeToggleProps<TValue>) => {
	const theme = useTheme();
	const { sx: groupSx, ...restGroupProps } = groupProps ?? {};

	return (
		<ToggleButtonGroup
			exclusive
			value={value ?? ""}
			onChange={(_, nextValue: TValue | null) => {
				if (nextValue == null) return;
				onChange(nextValue);
			}}
			size="small"
			color="primary"
			aria-label={ariaLabel}
			sx={{
				"& .MuiToggleButton-root": {
					textTransform: "none",
					fontWeight: 700,
					fontSize: "0.75rem",
					lineHeight: 1,
					px: 1.1,
					py: 0.65,
					minWidth: 46,
					borderRadius: "8px !important",
					border: `1px solid ${alpha(theme.palette.divider, 0.9)} !important`,
					color: "text.secondary",
					backgroundColor: theme.palette.background.paper,
				},
				"& .MuiToggleButton-root.Mui-selected": {
					color: "primary.main",
					backgroundColor: alpha(theme.palette.primary.main, 0.08),
					borderColor: `${theme.palette.primary.main} !important`,
				},
				"& .MuiToggleButton-root.Mui-selected:hover": {
					backgroundColor: alpha(theme.palette.primary.main, 0.12),
				},
				...groupSx,
			}}
			{...restGroupProps}
		>
			{options.map((option) => (
				<ToggleButton
					key={String(option.value)}
					value={option.value}
					aria-label={option.ariaLabel ?? option.label}
					{...buttonProps}
				>
					{option.label}
				</ToggleButton>
			))}
		</ToggleButtonGroup>
	);
};
